// R115 integ fix (D2) — the inline "Which home?" picker. The toolbar "+ Add" opens the create chooser with
// NO home (DateCell.listing_id ''): the chooser lists the view's create kinds at once and never gates on a
// home; each form that needs one asks for it here, inside the form (a Task may stay an office task). Feature
// modules import this file (core-owned, page-free) — never the page.
import type { CalendarCtx } from './registry';

/** The attribute every inline home picker carries (tests and forms find it by this). */
export const HOME_PICK_ATTR = 'data-stc-home-pick';

/** Option list: every home on the calendar — the search box ignored (ctx.allProperties) — by name (numeric-aware). `none` adds a first '' option (e.g. "No home — office task"). */
export function homeOptionsHtml(ctx: CalendarCtx, selected: string, none?: string): string {
  const props = [...(ctx.allProperties() || [])].sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { numeric: true }));
  const first = none
    ? `<option value=""${selected ? '' : ' selected'}>${ctx.esc(none)}</option>`
    : `<option value="" disabled${selected ? '' : ' selected'}>Pick a home…</option>`;
  return first + props.map(p => `<option value="${ctx.esc(p.listing_id)}"${p.listing_id === selected ? ' selected' : ''}>${ctx.esc(p.name)}</option>`).join('');
}

/**
 * The labelled picker, styled by the host form: `field` is the form's label class (ops 'opv-field', maint
 * 'mw-field'), `input` the class its controls carry. `none` makes the home optional (that option is the default).
 */
export function homePickHtml(ctx: CalendarCtx, o: { field: string; input?: string; none?: string; hint?: string }): string {
  return `<label class="${ctx.esc(o.field)} stc-homepick"><span>Which home?</span>`
    + `<select${o.input ? ` class="${ctx.esc(o.input)}"` : ''} ${HOME_PICK_ATTR}${o.none ? '' : ' required'} aria-label="Which home?">${homeOptionsHtml(ctx, '', o.none)}</select>`
    + `${o.hint ? `<small class="stc-homepick-hint">${ctx.esc(o.hint)}</small>` : ''}</label>`;
}

/** The picker in a form root, or null when the form was opened for a home already. */
export function homePickEl(root: ParentNode): HTMLSelectElement | null {
  return root.querySelector<HTMLSelectElement>(`[${HOME_PICK_ATTR}]`);
}

/** Calls fn(listingId) whenever the picker changes ('' = none picked / office). No-op without a picker. */
export function onHomePick(root: ParentNode, fn: (listingId: string) => void): void {
  const sel = homePickEl(root);
  if (sel) sel.addEventListener('change', () => fn(sel.value || ''));
}
